import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { PILLARS, CROSS_CUTTING } from "@/data/platform";
import { INDUSTRIES } from "@/data/industries";
import { PillarIcon } from "@/components/platform/PlatformPrimitives";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaMenu, setMegaMenu] = useState(null); // 'platform' | 'industries' | null
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mega menu on route change
  useEffect(() => {
    setMegaMenu(null);
    setOpen(false);
  }, [location.pathname]);

  const goHash = (id) => {
    setOpen(false);
    setMegaMenu(null);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    setTimeout(() => scrollToId(id), 60);
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openMega = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaMenu(key);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setMegaMenu(null), 120);
  };

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || megaMenu
          ? "backdrop-blur-xl bg-[#FDFCFB]/85 border-b border-[#E8E6E1]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid={NAV.logo}
          onClick={goHome}
          className="font-serif-display text-2xl md:text-[28px] tracking-tight text-[#1A1A1A]"
        >
          Heritex<span className="text-[#6B6A68]">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          <div
            onMouseEnter={() => openMega("platform")}
            onMouseLeave={scheduleClose}
            className="relative"
          >
            <Link
              to="/platform"
              data-testid="nav-platform"
              className="inline-flex items-center gap-1 text-sm text-[#1A1A1A] hover:text-[#6B6A68] py-2"
            >
              Platform
              <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: megaMenu === "platform" ? "rotate(180deg)" : "" }} />
            </Link>
          </div>

          <div
            onMouseEnter={() => openMega("industries")}
            onMouseLeave={scheduleClose}
            className="relative"
          >
            <Link
              to="/industries"
              data-testid="nav-industries"
              className="inline-flex items-center gap-1 text-sm text-[#1A1A1A] hover:text-[#6B6A68] py-2"
            >
              Industries
              <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: megaMenu === "industries" ? "rotate(180deg)" : "" }} />
            </Link>
          </div>

          <button
            data-testid={NAV.linkWork}
            onClick={() => goHash("portfolio")}
            className="text-sm text-[#1A1A1A] hover:text-[#6B6A68]"
          >
            Portfolio
          </button>
          <button
            data-testid={NAV.linkContact}
            onClick={() => goHash("contact")}
            className="text-sm text-[#1A1A1A] hover:text-[#6B6A68]"
          >
            Contact
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user && user !== false ? (
            <Link
              to="/app/crm"
              data-testid="nav-open-crm"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-[#1A1A1A] hover:bg-[#F5F4F0] transition-colors"
            >
              Open CRM
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          ) : user === false ? (
            <Link
              to="/login"
              state={{ from: location.pathname + location.hash }}
              data-testid="nav-signin"
              className="text-sm text-[#1A1A1A] hover:text-[#6B6A68] px-3 py-2"
            >
              Sign in
            </Link>
          ) : null}

          <button
            data-testid={NAV.cta}
            onClick={() => goHash("contact")}
            className="inline-flex items-center gap-2 pl-5 pr-4 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] transition-all duration-300 group shadow-[0_8px_20px_-8px_rgba(0,0,0,0.4)]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#7EE787] shadow-[0_0_8px_rgba(126,231,135,0.7)]" />
            Book Consultation
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          data-testid={NAV.menuToggle}
          onClick={() => setOpen((s) => !s)}
          className="md:hidden p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mega menu — Platform */}
      <AnimatePresence>
        {megaMenu === "platform" && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onMouseEnter={() => openMega("platform")}
            onMouseLeave={scheduleClose}
            className="hidden md:block absolute inset-x-0 top-full bg-[#FDFCFB] border-b border-[#E8E6E1] shadow-[0_22px_40px_-20px_rgba(0,0,0,0.15)]"
            data-testid="mega-platform"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-4 gap-5">
              {[...PILLARS, ...CROSS_CUTTING].map((p) => (
                <Link
                  key={p.slug}
                  to={`/platform/${p.slug}`}
                  className="group p-4 rounded-xl border border-transparent hover:border-[#E8E6E1] hover:bg-[#F5F4F0] transition-colors flex gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#F5F4F0] group-hover:bg-[#1A1A1A] group-hover:text-[#FDFCFB] flex items-center justify-center shrink-0 transition-colors">
                    <PillarIcon name={p.icon} className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif-display text-base text-[#1A1A1A] tracking-tight">
                      {p.name}
                    </div>
                    <div className="text-[11px] text-[#6B6A68] mt-0.5 leading-snug">
                      {p.tagline}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {megaMenu === "industries" && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onMouseEnter={() => openMega("industries")}
            onMouseLeave={scheduleClose}
            className="hidden md:block absolute inset-x-0 top-full bg-[#FDFCFB] border-b border-[#E8E6E1] shadow-[0_22px_40px_-20px_rgba(0,0,0,0.15)]"
            data-testid="mega-industries"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-4 gap-5">
              {INDUSTRIES.map((ind) => (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="group p-4 rounded-xl border border-transparent hover:border-[#E8E6E1] hover:bg-[#F5F4F0] transition-colors"
                >
                  <div className="font-serif-display text-base text-[#1A1A1A] tracking-tight">
                    {ind.name}
                  </div>
                  {ind.brand && (
                    <div className="font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#6B6A68] mt-1">
                      {ind.brand}
                    </div>
                  )}
                  <div className="text-[11px] text-[#6B6A68] mt-1 leading-snug">
                    {ind.tagline}
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-[#FDFCFB] border-t border-[#E8E6E1] overflow-hidden max-h-[80vh] overflow-y-auto"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              <Link
                to="/platform"
                onClick={() => setOpen(false)}
                className="text-left font-serif-display text-2xl text-[#1A1A1A] py-1.5 flex items-center justify-between"
              >
                Platform <ArrowUpRight className="w-5 h-5" />
              </Link>
              <Link
                to="/industries"
                onClick={() => setOpen(false)}
                className="text-left font-serif-display text-2xl text-[#1A1A1A] py-1.5 flex items-center justify-between"
              >
                Industries <ArrowUpRight className="w-5 h-5" />
              </Link>
              <button
                onClick={() => goHash("portfolio")}
                className="text-left font-serif-display text-2xl text-[#1A1A1A] py-1.5"
              >
                Portfolio
              </button>
              <button
                onClick={() => goHash("contact")}
                className="text-left font-serif-display text-2xl text-[#1A1A1A] py-1.5"
              >
                Contact
              </button>

              {user && user !== false ? (
                <Link
                  to="/app/crm"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-between text-left font-serif-display text-2xl text-[#1A1A1A] py-1.5"
                >
                  Open CRM <ArrowUpRight className="w-5 h-5" />
                </Link>
              ) : user === false ? (
                <Link
                  to="/login"
                  state={{ from: location.pathname + location.hash }}
                  onClick={() => setOpen(false)}
                  className="text-left font-serif-display text-2xl text-[#1A1A1A] py-1.5"
                >
                  Sign in
                </Link>
              ) : null}

              <button
                data-testid={`${NAV.cta}-mobile`}
                onClick={() => goHash("contact")}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm shadow-[0_12px_30px_-10px_rgba(0,0,0,0.5)]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#7EE787]" />
                Book a Free Consultation
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
